import React, { useRef, useState } from 'react';
import { Button, DatePicker, Timeline } from 'antd';
import { blue, green, red } from '@ant-design/colors';
import {
	ArrowDownOutlined,
	ArrowUpOutlined,
	CheckCircleOutlined,
	ClockCircleOutlined,
	CloseCircleOutlined,
	DollarCircleOutlined,
	EyeOutlined,
	PrinterOutlined,
} from '@ant-design/icons';
import styled from 'styled-components';
import { Address, Client, ClientOrder, Container, getReadablePriceType, PriceType } from 'types';
import PageSection from 'components/page/pageSection';
import SearchSelector from '../../components/searchSelectorNew';
import { ClientOrdersPrint } from '../../prints';
import { useReactToPrint } from 'react-to-print';
import { Formatting } from '../../utils';
import moment from 'moment';
import { Link } from 'react-router-dom';
import { AsyncLink } from 'components';

const Wrapper = styled.div`
	> div.page-section > div {
		margin-bottom: 4px;
		padding: 4px 0;
	}
`;

interface Props {
	client: Client;
	orders: ClientOrder[];
	addresses: Address[];
	containers: Container[];
	payOrder: (id: number) => Promise<void>;
}

export const OrdersSection: React.FC<Props> = ({ client, orders, addresses, containers, payOrder }) => {
	const [address, setAddress] = useState<string | undefined>();
	const [onlyUnpaid, setOnlyUnpaid] = useState<boolean>();
	const [after, setAfter] = useState<moment.Moment | null>(null);
	const [before, setBefore] = useState<moment.Moment | null>(null);
	const [sort, setSort] = useState<'newest' | 'oldest'>('newest');

	const printRef = useRef();
	const handlePrint = useReactToPrint({
		content: () => printRef.current!,
	});

	const filteredOrders = orders.filter((order) => {
		if (address && order.address !== address) {
			return false;
		}

		const leftToPay = order.grossPrice - order.paid;
		if (onlyUnpaid && leftToPay <= 0) {
			return false;
		}

		if (after && moment(order.date).isBefore(after, 'day')) {
			return false;
		}

		if (before && moment(order.date).isAfter(before, 'day')) {
			return false;
		}

		return true;
	});
	if (sort === 'newest') {
		filteredOrders.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
	} else {
		filteredOrders.sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
	}

	const events = filteredOrders.map((order) => {
		const icon = order.doneDate ? (
			<CheckCircleOutlined style={{ color: blue[5] }} />
		) : (
			<ClockCircleOutlined style={{ color: blue[5] }} />
		);

		const date = (
			<>
				{Formatting.epochToDate(order.date, moment(order.date).hours() === 0)}
				{/* {event.claiming_date && ' - '} */}
				{/* {event.claiming_date && Formatting.epochToDate(event.claiming_date, moment(event.claiming_date).hours() === 0)} */}
			</>
		);

		const container = order.containerId ? containers.find((c) => c.id === order.containerId) : undefined;
		const title = container ? `${order.address} - nr: ${container.name}` : order.address;
		const price = order.priceType === PriceType.net ? order.netPrice : order.grossPrice;

		return (
			<Timeline.Item key={`${order.type}#${order.id}`} dot={icon}>
				<span>{date}</span> - {title}. {Formatting.money(price)} {getReadablePriceType(order.priceType)}.{' '}
				{order.grossPrice > order.paid &&
					(order.type === 'classic' ? (
						<AsyncLink color={green[5]!} onClick={() => payOrder(order.id)}>
							Oznacz jako zapłacone
						</AsyncLink>
					) : (
						<p style={{ color: red[5] }}>(Nieopłacone)</p>
					))}{' '}
				{<Link to={`/panel/orders/${order.id}/${order.type}`}>Szczegóły</Link>}
			</Timeline.Item>
		);
	});

	return (
		<Wrapper>
			<PageSection
				title="Zamówienia"
				width={878}
				select={
					<Button type="primary" size="large" onClick={() => handlePrint()} icon={<PrinterOutlined />}>
						Drukuj wykaz
					</Button>
				}
			>
				<div className="flex flex-col gap-2">
					<div className="flex items-center gap-2">
						<p>Adres:</p>
						<SearchSelector
							id="name"
							width="100%"
							allowClear
							search={(search, e) => {
								if (!e.name) {
									return false;
								}

								const s = search.toLocaleLowerCase();
								return e.name.toLocaleLowerCase().includes(s);
							}}
							elements={[...addresses, { name: undefined, id: 'all' }]}
							buildElement={(address) => address.name ?? 'Wszystkie'}
							value={address}
							onChange={(a) => setAddress(a === 'all' ? undefined : a)}
						/>
					</div>
					<div className="flex items-center gap-2">
						<p>Data:</p>
						<DatePicker picker="date" placeholder="Data od" size="large" value={after} onChange={setAfter} />
						<DatePicker picker="date" placeholder="Data do" size="large" value={before} onChange={setBefore} />
						<Button
							type="primary"
							size="large"
							onClick={() => {
								setAfter(null);
								setBefore(null);
							}}
						>
							<CloseCircleOutlined />
						</Button>
					</div>
					<div className="flex gap-2 items-stretch">
						<Button
							className="flex-1"
							type="primary"
							size="large"
							onClick={() => setSort(sort === 'newest' ? 'oldest' : 'newest')}
							icon={sort === 'newest' ? <ArrowDownOutlined /> : <ArrowUpOutlined />}
						>
							Sortowane od {sort === 'newest' ? 'najnowszych' : 'najstarszych'}
						</Button>
						<Button
							className="flex-1"
							type="primary"
							size="large"
							onClick={() => setOnlyUnpaid((u) => !u)}
							icon={!onlyUnpaid ? <DollarCircleOutlined /> : <EyeOutlined />}
						>
							{!onlyUnpaid ? 'Pokaż tylko nieopłacone' : 'Pokaż wszystkie'}
						</Button>
					</div>
				</div>
				<ClientOrdersPrint ref={printRef} client={client} orders={filteredOrders} />
				<Timeline>{events}</Timeline>
			</PageSection>
		</Wrapper>
	);
};
